// Copyright (c) Meta Platforms, Inc. and affiliates.

'use client';

/**
 * @file NumberInput.tsx
 * @input Uses React, useId, useState, useMemo, useCallback, Field, Icon, InputGroupContext
 * @output Exports NumberInput component, NumberInputProps
 * @position Core implementation; consumed by index.ts, tested by NumberInput.test.tsx
 *
 * SYNC: When modified, update these files to stay in sync:
 * - /packages/core/src/NumberInput/NumberInput.doc.mjs (props table, features, implementation notes)
 * - /packages/core/src/NumberInput/NumberInput.test.tsx (tests for new/changed behavior)
 * - /packages/core/src/NumberInput/index.ts (exports if types change)
 * - /apps/storybook/stories/NumberInput.stories.tsx (storybook stories)
 * - /packages/cli/assets/templates/blocks/components/NumberInput/ (showcase blocks)
 */
import { useId, useState, useMemo, useCallback, useRef } from 'react';
import * as stylex from '@stylexjs/stylex';
import "../theme/tokens.stylex.js";
import { colorVars, sizeVars, spacingVars, radiusVars, typographyVars, typeScaleVars, borderVars } from "../theme/tokens.stylex.js";
import { Field, InputClearButton, inputWrapperStyles, inputStatusBorderStyles, inputStatusHoverShadowStyles, inputStatusFocusWithinStyles } from "../Field/index.js";
import { Icon, renderIconSlot } from "../Icon/index.js";
import { VisuallyHidden } from "../VisuallyHidden/index.js";
import { useTooltip } from "../Tooltip/index.js";
import { getInputARIA } from "../utils/index.js";
import { useSize } from "../SizeContext/SizeContext.js";
import { useInputContainer } from "../hooks/useInputContainer.js";
import { useInputStatusIcon } from "../hooks/useInputStatusIcon.js";
import { useInputGroup } from "../InputGroup/InputGroupContext.js";
const styles = {
  wrapper: {
    kY2c9j: "x1vjfegm",
    $$css: true
  },
  wrapperWithNumberSteppers: {
    kwRFfy: "xyri2b",
    $$css: true
  },
  incrementIcon: {
    k3aq6I: "x19jd1h0",
    $$css: true
  }
};
const sizeStyles = {
  sm: {
    kZKoxP: "x6k0iem",
    $$css: true
  },
  md: {
    kZKoxP: "x1ueg155",
    $$css: true
  },
  lg: {
    kZKoxP: "xssyfek",
    $$css: true
  }
};
import { groupStyles } from "../InputGroup/groupStyles.js";

// Re-export shared types for convenience

import { mergeProps, mergeRefs } from "../utils/index.js";
import { themeProps } from "../utils/themeProps.js";
import { useTranslator } from "../i18n/index.js";

/**
 * Without `hasClear`, onChange only receives valid numbers.
 * With `hasClear`, onChange also receives `null` when the user clears the input.
 */
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
/**
 * Parse and validate a string input as a number.
 * Returns null if the input is not a valid number or fails validation.
 */
function parseNumberInput(input, options) {
  const trimmed = input.trim();
  if (trimmed === '' || trimmed === '-') {
    return null;
  }
  const num = Number(trimmed);
  if (!Number.isFinite(num)) {
    return null;
  }

  // Check integer constraint
  if (options.isIntegerOnly && !Number.isInteger(num)) {
    return null;
  }

  // Check min constraint
  if (options.min != null && num < options.min) {
    return null;
  }

  // Check max constraint
  if (options.max != null && num > options.max) {
    return null;
  }
  return num;
}
function getDecimalPlaces(value) {
  const [coefficient, exponentText] = String(value).toLowerCase().split('e');
  const fractionLength = coefficient.split('.')[1]?.length ?? 0;
  const exponent = exponentText == null ? 0 : Number(exponentText);
  return Math.max(0, fractionLength - exponent);
}
function getEffectiveStep(step, isIntegerOnly) {
  if (step == null || !Number.isFinite(step) || step <= 0 || isIntegerOnly && !Number.isInteger(step)) {
    return 1;
  }
  return step;
}
function getSteppedValue({
  currentValue,
  direction,
  min,
  max,
  step,
  isIntegerOnly
}) {
  const effectiveStep = getEffectiveStep(step, isIntegerOnly);
  const stepBase = min != null && (!isIntegerOnly || Number.isInteger(min)) ? min : 0;
  let nextValue;
  if (currentValue == null) {
    nextValue = direction === 1 ? min ?? 0 : max ?? 0;
    if (isIntegerOnly) {
      nextValue = direction === 1 ? Math.ceil(nextValue) : Math.floor(nextValue);
    }
  } else {
    const stepPosition = (currentValue - stepBase) / effectiveStep;
    const tolerance = Number.EPSILON * Math.max(1, Math.abs(stepPosition)) * 4;
    const nextStepPosition = direction === 1 ? Math.floor(stepPosition + tolerance) + 1 : Math.ceil(stepPosition - tolerance) - 1;
    nextValue = stepBase + nextStepPosition * effectiveStep;
  }
  if (min != null) {
    nextValue = Math.max(min, nextValue);
  }
  if (max != null) {
    nextValue = Math.min(max, nextValue);
  }
  if (!Number.isFinite(nextValue)) {
    return currentValue;
  }
  const precision = Math.min(12, Math.max(getDecimalPlaces(effectiveStep), getDecimalPlaces(stepBase)));
  const roundedValue = Number(nextValue.toFixed(precision));
  if (isIntegerOnly && !Number.isInteger(roundedValue)) {
    return currentValue;
  }
  return Object.is(roundedValue, -0) ? 0 : roundedValue;
}

/**
 * A number input component for collecting numeric user input.
 * Only calls onChange when the entered value passes validation.
 *
 * @example
 * ```
 * <NumberInput label="Quantity" value={quantity} onChange={setQuantity} />
 * <NumberInput label="Price" value={price} onChange={setPrice} min={0} step={0.01} />
 * ```
 */
export function NumberInput({
  label,
  isLabelHidden = false,
  description,
  isOptional = false,
  isRequired = false,
  isDisabled = false,
  isReadOnly = false,
  disabledMessage,
  startIcon,
  labelIcon,
  status,
  statusVariant = 'attached',
  size: sizeProp,
  onChange,
  value,
  placeholder,
  labelTooltip,
  hasAutoFocus = false,
  htmlName,
  autoComplete,
  min,
  max,
  step,
  formatValue,
  isWheelEnabled = true,
  hasNumberSteppers = false,
  units,
  isIntegerOnly = false,
  onFocus,
  onBlur,
  hasClear,
  onEnter,
  onKeyDown,
  width,
  xstyle,
  className,
  style,
  ref,
  ...rest
}) {
  const t = useTranslator();
  const size = useSize(sizeProp, 'md');
  const id = useId();
  const inputLabelID = useId();
  const descriptionID = useId();
  const statusMessageID = useId();
  const unitsID = useId();
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const inputGroup = useInputGroup();

  // Pending input while user is typing (null = show formatted value)
  const [pendingInput, setPendingInput] = useState(null);
  const [isFocused, setIsFocused] = useState(false);

  // Disabled-reason tooltip. Disabled controls swallow pointer events, so the
  // tooltip listeners attach to the input container (which already exists) and
  // the input stays perceivable via aria-disabled instead of the native
  // disabled attribute. The field is made read-only so it can't be typed into,
  // and value mutation is blocked by the isDisabled guard in the handlers.
  const showsDisabledMessage = isDisabled && !!disabledMessage;
  const disabledMessageTooltip = useTooltip({
    placement: 'above',
    // The container div is not naturally focusable; focusin bubbles up from
    // the input, so always attach focus listeners.
    focusTrigger: 'always',
    isEnabled: showsDisabledMessage
  });
  const {
    statusIcon,
    describedBy: statusTooltipDescribedBy
  } = useInputStatusIcon({
    status,
    statusVariant,
    isInGroup: !!inputGroup
  });
  const {
    ariaLabelledBy,
    ariaDescribedBy
  } = getInputARIA(inputLabelID, [description ? descriptionID : null,
  // The status message element is rendered by Field, which is skipped
  // inside an InputGroup — only reference it when it actually exists.
  !inputGroup && statusVariant !== 'tooltip' && status?.message ? statusMessageID : null,
  // The tooltip variant renders no message box; describe the input by the
  // tooltip's content instead so the status is still announced.
  statusTooltipDescribedBy, units ? unitsID : null, showsDisabledMessage ? disabledMessageTooltip.describedBy : null], inputGroup);
  const formattedValue = useMemo(() => {
    if (value == null) {
      return '';
    }
    return formatValue?.(value) ?? String(value);
  }, [formatValue, value]);

  // Preserve pending text while editing. Otherwise show the formatted value
  // at rest and the raw numeric value while focused so it remains editable.
  const displayValue = useMemo(() => {
    if (pendingInput !== null) {
      return pendingInput;
    }
    if (value == null) {
      return '';
    }
    return isFocused ? String(value) : formattedValue;
  }, [formattedValue, isFocused, pendingInput, value]);

  // Check if current pending input is valid (for styling purposes)
  const isInputValid = useMemo(() => {
    if (pendingInput === null || !pendingInput.trim()) {
      return true;
    }
    return parseNumberInput(pendingInput, {
      min,
      max,
      isIntegerOnly
    }) !== null;
  }, [pendingInput, min, max, isIntegerOnly]);

  // Handle input text change - update immediately if valid
  const handleInputChange = useCallback(e => {
    // Value can't change while showing a disabled message (the field is
    // read-only and non-native-disabled), but guard the handler too so the
    // pending value and onChange never fire.
    if (isDisabled || isReadOnly) {
      return;
    }
    const newValue = e.target.value;
    setPendingInput(newValue);

    // If the input is valid, update immediately
    const parsed = parseNumberInput(newValue, {
      min,
      max,
      isIntegerOnly
    });
    if (parsed !== null && parsed !== value) {
      onChange(parsed);
    }
  }, [value, onChange, min, max, isIntegerOnly, isDisabled, isReadOnly]);

  // Handle focus
  const handleFocus = useCallback(e => {
    setIsFocused(true);
    onFocus?.(e);
  }, [onFocus]);

  // Handle blur - validate and clear pending input
  const handleBlur = useCallback(e => {
    if (pendingInput !== null) {
      if (hasClear && pendingInput.trim() === '') {
        // Keyboard clearing honors the clearable contract: an emptied
        // input commits null instead of silently reverting on blur.
        if (value != null) {
          onChange(null);
        }
      } else {
        const parsed = parseNumberInput(pendingInput, {
          min,
          max,
          isIntegerOnly
        });
        if (parsed !== null && parsed !== value) {
          onChange(parsed);
        }
      }
    }

    // Clear pending input - display will revert to formatted value
    setPendingInput(null);
    setIsFocused(false);
    onBlur?.(e);
  }, [pendingInput, value, onChange, min, max, isIntegerOnly, onBlur, hasClear]);
  const valueForStepping = useMemo(() => {
    if (pendingInput === null) {
      return value ?? null;
    }
    if (pendingInput.trim() === '') {
      return null;
    }
    return parseNumberInput(pendingInput, {
      min,
      max,
      isIntegerOnly
    }) ?? value ?? null;
  }, [isIntegerOnly, max, min, pendingInput, value]);
  const getNextValue = useCallback(direction => getSteppedValue({
    currentValue: valueForStepping,
    direction,
    min,
    max,
    step,
    isIntegerOnly
  }), [isIntegerOnly, max, min, step, valueForStepping]);
  const stepValue = useCallback(direction => {
    // A read-only field is not steppable by any route: keyboard, wheel, or
    // the stepper buttons all land here.
    if (isDisabled || isReadOnly) {
      return;
    }
    const nextValue = getNextValue(direction);
    if (nextValue == null) {
      return;
    }
    setPendingInput(null);
    if (nextValue !== value) {
      onChange(nextValue);
    }
  }, [getNextValue, isDisabled, isReadOnly, onChange, value]);

  // Handle keyboard events
  const handleKeyDown = useCallback(e => {
    const hasModifier = e.altKey || e.ctrlKey || e.metaKey || e.shiftKey;
    if (!hasModifier && (e.key === 'ArrowUp' || e.key === 'ArrowDown')) {
      onKeyDown?.(e);
      if (e.defaultPrevented) {
        return;
      }
      e.preventDefault();
      stepValue(e.key === 'ArrowUp' ? 1 : -1);
      return;
    }
    if (e.key === 'Enter') {
      // Validate and commit on Enter
      if (pendingInput !== null) {
        if (hasClear && pendingInput.trim() === '') {
          // Same clearable contract as blur: Enter on an emptied input
          // commits null instead of reverting.
          if (value != null) {
            onChange(null);
          }
        } else {
          const parsed = parseNumberInput(pendingInput, {
            min,
            max,
            isIntegerOnly
          });
          if (parsed !== null && parsed !== value) {
            onChange(parsed);
          }
        }
      }
      onEnter?.();
    }
    onKeyDown?.(e);
  }, [pendingInput, value, onChange, min, max, isIntegerOnly, onEnter, onKeyDown, hasClear, stepValue]);

  // React's delegated wheel listener can be passive, so use a native,
  // explicitly non-passive listener to prevent page scrolling only when this
  // focused input is intentionally consuming the gesture to step its value.
  const wheelListenerRef = useCallback(input => {
    if (input == null || !isWheelEnabled) {
      return;
    }
    const handleWheel = event => {
      // Bail before preventDefault so a read-only input never swallows the
      // page scroll it cannot act on.
      if (document.activeElement !== input || isDisabled || isReadOnly || event.deltaY === 0 || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      stepValue(event.deltaY < 0 ? 1 : -1);
    };
    input.addEventListener('wheel', handleWheel, {
      passive: false
    });
    return () => input.removeEventListener('wheel', handleWheel);
  }, [isDisabled, isReadOnly, isWheelEnabled, stepValue]);
  const mergedInputRef = useMemo(() => mergeRefs(ref, inputRef, wheelListenerRef), [ref, wheelListenerRef]);
  const canIncrement = getNextValue(1) !== valueForStepping;
  const canDecrement = getNextValue(-1) !== valueForStepping;

  // Handle clear button click
  const handleClear = useCallback(() => {
    if (hasClear) {
      onChange(null);
    }
    setPendingInput(null);
    inputRef.current?.focus();
  }, [hasClear, onChange]);

  // Focus input when clicking anywhere on the wrapper (icons, padding, etc.)
  const {
    onClick: handleWrapperClick,
    onMouseUp: handleWrapperMouseUp
  } = useInputContainer({
    containerRef,
    inputRef,
    disabled: isDisabled
  });
  const inputWrapper = /*#__PURE__*/_jsxs("div", {
    ref: el => {
      containerRef.current = el;
      // Anchor + hover/focus listeners for the disabled-message tooltip.
      // Handlers are gated internally by isEnabled, and anchor names
      // compose, so attaching unconditionally is safe.
      disabledMessageTooltip.ref(el);
    },
    onClick: handleWrapperClick,
    onMouseUp: handleWrapperMouseUp,
    ...mergeProps(themeProps('number-input', {
      size,
      status: status?.type ?? null,
      disabled: isDisabled ? 'disabled' : null,
      readonly: isReadOnly ? 'readonly' : null
    }), stylex.props(inputWrapperStyles.base, styles.wrapper, hasNumberSteppers && styles.wrapperWithNumberSteppers, sizeStyles[size], isDisabled && inputWrapperStyles.disabled, status && inputStatusBorderStyles[status.type], status && !isDisabled && inputStatusHoverShadowStyles[status.type], status && inputStatusFocusWithinStyles[status.type], inputGroup && groupStyles.inGroup, xstyle), className, style),
    children: [startIcon && renderIconSlot(startIcon, {
      size: 'sm',
      color: 'secondary'
    }), inputGroup && /*#__PURE__*/_jsx(VisuallyHidden, {
      id: inputLabelID,
      children: label
    }), /*#__PURE__*/_jsx("input", {
      ...rest,
      ref: mergedInputRef,
      id: id,
      name: isDisabled || formatValue ? undefined : htmlName,
      type: "text",
      inputMode: isIntegerOnly ? 'numeric' : 'decimal',
      role: "spinbutton",
      autoComplete: autoComplete,
      value: displayValue,
      onChange: handleInputChange,
      onFocus: handleFocus,
      onBlur: handleBlur,
      onKeyDown: handleKeyDown,
      placeholder: placeholder
      // With a disabledMessage the input keeps focusability via aria-disabled
      // so the reason is focus-discoverable; readOnly + the handler guards
      // keep the value from changing.
      ,
      disabled: isDisabled && !showsDisabledMessage,
      "aria-disabled": showsDisabledMessage ? 'true' : undefined,
      readOnly: isReadOnly || showsDisabledMessage || undefined,
      autoFocus: hasAutoFocus,
      "data-autofocus": hasAutoFocus || undefined,
      "aria-valuemin": min ?? undefined,
      "aria-valuemax": max ?? undefined,
      "aria-valuenow": value ?? undefined,
      "aria-valuetext": value == null || !formatValue ? undefined : formattedValue,
      "aria-describedby": ariaDescribedBy,
      "aria-required": isRequired === true ? 'true' : undefined,
      "aria-invalid": status?.type === 'error' || !isInputValid ? 'true' : undefined,
      "aria-labelledby": ariaLabelledBy,
      ...{
        0: {
          className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx x1tgivj0 xjbqb8w x1a2a7pz xeyghm5"
        },
        2: {
          className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx x1tgivj0 xjbqb8w x1a2a7pz xeyghm5 x1h6gzvc"
        },
        1: {
          className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx xjbqb8w x1a2a7pz xeyghm5 xv1l7n4"
        },
        3: {
          className: "x1lliihq x98rzlu xeuugli xc342km xng3xce x1717udv x9ynric xjm74w1 x6pjikd xw6l6zx xjbqb8w x1a2a7pz xeyghm5 x1h6gzvc xv1l7n4"
        }
      }[!!isDisabled << 1 | !!!isInputValid << 0]
    }), formatValue && htmlName && !isDisabled && /*#__PURE__*/_jsx("input", {
      type: "hidden",
      name: htmlName,
      value: value == null ? '' : String(value)
    }), units && /*#__PURE__*/_jsx("span", {
      id: unitsID,
      ...{
        className: "x9ynric xjm74w1 xw6l6zx xv1l7n4 x2lah0s"
      },
      children: units
    }), /*#__PURE__*/_jsx(VisuallyHidden, {
      as: "div",
      role: "alert",
      "aria-live": "assertive",
      children: !isInputValid ? 'Invalid number' : ''
    }), hasClear && value != null && !isDisabled && !isReadOnly && /*#__PURE__*/_jsx(InputClearButton, {
      label: t('@astryx.numberInput.clearLabel', {
        label
      }),
      onClick: handleClear
    }), statusIcon, hasNumberSteppers && /*#__PURE__*/_jsxs("div", {
      ...{
        className: "xkh2ocl x78zum5 xdt5ytf x2lah0s x12xnipv x10hv0ee xgbv0en x1t7ytsu x14ydlgx xb3r6kr xxbepff x14i3lts"
      },
      children: [/*#__PURE__*/_jsx("button", {
        type: "button",
        tabIndex: -1,
        disabled: isDisabled || isReadOnly || !canIncrement,
        "aria-label": t('@astryx.numberInput.incrementLabel', {
          label
        }),
        onPointerDown: event => event.preventDefault(),
        onClick: () => {
          inputRef.current?.focus();
          stepValue(1);
        },
        ...{
          0: {
            className: "x9f619 x78zum5 x6s0dn4 xl56j7k x98rzlu x2lwn1j x1717udv x1ghz6dp xc342km xng3xce xv9yike x10xzikg x1ilzqfv xq8i9tn x1ypdohk x1a2a7pz"
          },
          1: {
            className: "x9f619 x78zum5 x6s0dn4 xl56j7k x98rzlu x2lwn1j x1717udv x1ghz6dp xc342km xng3xce x10xzikg x1a2a7pz xqa6c3m x1h6gzvc x18o3ruo"
          }
        }[!!(isDisabled || isReadOnly || !canIncrement) << 0],
        children: /*#__PURE__*/_jsx(Icon, {
          icon: "chevronDown",
          size: "xsm",
          color: "inherit",
          xstyle: styles.incrementIcon
        })
      }), /*#__PURE__*/_jsx("button", {
        type: "button",
        tabIndex: -1,
        disabled: isDisabled || isReadOnly || !canDecrement,
        "aria-label": t('@astryx.numberInput.decrementLabel', {
          label
        }),
        onPointerDown: event => event.preventDefault(),
        onClick: () => {
          inputRef.current?.focus();
          stepValue(-1);
        },
        ...{
          0: {
            className: "x9f619 x78zum5 x6s0dn4 xl56j7k x98rzlu x2lwn1j x1717udv x1ghz6dp xc342km xng3xce xv9yike x10xzikg x1ilzqfv xq8i9tn x1ypdohk x1a2a7pz x11xkdxz x13fuv20 x1mly77v"
          },
          1: {
            className: "x9f619 x78zum5 x6s0dn4 xl56j7k x98rzlu x2lwn1j x1717udv x1ghz6dp xc342km xng3xce x10xzikg x1a2a7pz x11xkdxz x13fuv20 x1mly77v xqa6c3m x1h6gzvc x18o3ruo"
          }
        }[!!(isDisabled || isReadOnly || !canDecrement) << 0],
        children: /*#__PURE__*/_jsx(Icon, {
          icon: "chevronDown",
          size: "xsm",
          color: "inherit"
        })
      })]
    })]
  });
  if (inputGroup) {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [inputWrapper, showsDisabledMessage && disabledMessageTooltip.renderTooltip(disabledMessage)]
    });
  }
  return /*#__PURE__*/_jsxs(Field, {
    label: label,
    isLabelHidden: isLabelHidden,
    description: description,
    inputID: id,
    descriptionID: description ? descriptionID : undefined,
    isOptional: isOptional,
    isRequired: isRequired,
    isDisabled: isDisabled,
    labelIcon: labelIcon,
    status: status ? {
      type: status.type,
      message: status.message,
      messageID: status.message ? statusMessageID : undefined
    } : undefined,
    statusVariant: statusVariant,
    labelTooltip: labelTooltip,
    width: width,
    children: [inputWrapper, showsDisabledMessage && disabledMessageTooltip.renderTooltip(disabledMessage)]
  });
}
NumberInput.displayName = 'NumberInput';